import { createPortal } from 'react-dom'
import { useEffect, useMemo, useState } from 'react'

import { Box, Dialog, DialogActions, DialogContent, DialogTitle, OutlinedInput, Stack, Typography } from '@mui/material'
import toast from 'react-hot-toast'

import { track, eventTypes } from 'torqcloud-ui'

import { StyledButton } from '../button/StyledButton'

// API
import useAgentApi from '../../../api/hooks/useAgentApi'
import type { PatchSecretRequest, SecretResponse } from '@api/generated/internal/internalApi'

export type SecretDialogMode = 'ADD' | 'EDIT'

export interface SecretDialogPropsType {
  title: string
  type: SecretDialogMode
  confirmButtonName: string
  secret?: SecretResponse
}

export interface SecretDialogProps {
  show: boolean
  dialogProps: SecretDialogPropsType
  onCancel: () => void
  onConfirm: () => void
}

function toErrorText(error: unknown): string {
  if (error instanceof Error) return error.message
  try {
    return typeof error === 'string' ? error : JSON.stringify(error)
  } catch {
    return String(error)
  }
}

const SECRET_MASK = '••••••••'

export default function SecretDialog({ show, dialogProps, onCancel, onConfirm }: SecretDialogProps) {
  const portalElement = document.getElementById('portal')

  const agentApi = useAgentApi()

  const originalName = useMemo(() => (dialogProps.type === 'EDIT' ? dialogProps.secret?.name ?? '' : ''), [dialogProps])

  const [name, setName] = useState<string>('')
  const [value, setValue] = useState<string>('')

  useEffect(() => {
    if (dialogProps.type === 'EDIT' && dialogProps.secret) {
      setName(dialogProps.secret.name)
      setValue('')
      return
    }

    if (dialogProps.type === 'ADD') {
      setName('')
      setValue('')
    }
  }, [dialogProps])

  const createSecret = async () => {
    try {
      const trimmedName = name.trim()
      if (!trimmedName) return
      if (!value) return

      const { data, error } = await agentApi.secrets.createSecret({ name: trimmedName, value })
      if (error) throw error

      if (data) {
        toast.success('Secret created')
        track(eventTypes.SECRET_CREATED)
      }

      onConfirm()
    } catch (error: unknown) {
      const errorText = toErrorText(error)
      toast.error(`Failed to create secret: ${errorText}`)
      onCancel()
    }
  }

  const patchSecret = async () => {
    try {
      if (!dialogProps.secret) return

      const trimmedName = name.trim()

      const patchBody: PatchSecretRequest = {}
      if (trimmedName && trimmedName !== originalName) {
        patchBody.name = trimmedName
      }
      if (value) {
        patchBody.value = value
      }

      if (Object.keys(patchBody).length === 0) {
        toast('No changes to save')
        return
      }

      const { data, error } = await agentApi.secrets.patchSecret(dialogProps.secret.id, patchBody)
      if (error) throw error

      if (data) {
        toast.success('Secret saved')
        track(eventTypes.SECRET_UPDATED)
      }

      onConfirm()
    } catch (error: unknown) {
      const errorText = toErrorText(error)
      toast.error(`Failed to save secret: ${errorText}`)
      onCancel()
    }
  }

  const component = show ? (
    <Dialog fullWidth maxWidth="sm" open={show} onClose={onCancel} aria-labelledby="secret-dialog-title">
      <DialogTitle sx={{ fontSize: '1rem' }} id="secret-dialog-title">
        {dialogProps.title}
      </DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <Box>
            <Stack sx={{ position: 'relative' }} direction="row">
              <Typography variant="overline">Name</Typography>
            </Stack>
            <OutlinedInput
              id="secretName"
              type="text"
              fullWidth
              placeholder="Secret name"
              value={name}
              name="secretName"
              onChange={e => setName(e.target.value)}
            />
          </Box>

          <Box>
            <Stack sx={{ position: 'relative' }} direction="row">
              <Typography variant="overline">Value</Typography>
              {dialogProps.type === 'EDIT' && (
                <Typography sx={{ ml: 1, alignSelf: 'center' }} variant="caption" color="text.secondary">
                  Leave blank to keep existing value
                </Typography>
              )}
            </Stack>
            <OutlinedInput
              id="secretValue"
              type="password"
              fullWidth
              placeholder={dialogProps.type === 'EDIT' ? SECRET_MASK : 'Secret value'}
              value={value}
              name="secretValue"
              onChange={e => setValue(e.target.value)}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <StyledButton variant="contained" onClick={() => (dialogProps.type === 'ADD' ? createSecret() : patchSecret())}>
          {dialogProps.confirmButtonName}
        </StyledButton>
      </DialogActions>
    </Dialog>
  ) : null

  return portalElement ? createPortal(component, portalElement) : null
}
